import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import {archetypeLabel} from "../support/strategyLabels";
import {pluralDays, pluralItems} from "../utils/plural";

/**
 * Предлагаемое действие на главной. Величина разделена (R18): `count` — количество
 * объектов, `score` — балл 0–100. Раньше одно поле несло и то и другое, и кнопка
 * «Подтянуть соответствие (50)» читалась как «50 вещей» вместо «50 из 100».
 */
interface HomeAction {
    code: string;
    count?: number | null;
    score?: number | null;
}

interface HomeSummary {
    strategyConfigured: boolean;
    archetype: string | null;
    adherenceScore: number | null;
    disposalCount: number;
    gapsCount: number;
    /** null — история носки ещё не набрана; ноль на месте «не посчитано» вводил в заблуждение (Q6). */
    deadStockCount: number | null;
    /** Бэкенд оставляет число 0 ради iOS и поднимает флаг — «залежалось» не посчитано. */
    deadStockNotComputed?: boolean;
    /** Окно анализа «давно не носили» из стратегии, дней — настройка, а не срок ожидания. */
    deadStockThresholdDays?: number | null;
    /** Активных вещей — знаменатель подписи «нет отметок носки у N из M». */
    activeItemsCount?: number | null;
    /** Вещей без единой отметки носки — настоящая причина, почему «залежалось» не посчитано. */
    itemsWithoutWearRecords?: number | null;
    /** Остаток дней без новых отметок; null — истории достаточно либо бэкенд его не отдал. */
    historyDaysRemaining?: number | null;
    /** 0 при duplicatesNotComputed — состояние читать из флага, не из числа. */
    duplicateGroupsCount: number;
    duplicatesNotComputed?: boolean;
    duplicatesUnindexedCount?: number;
    /** Состояние проверки дублей: NOT_CHECKED | PARTIAL | NO_MATCHES | MATCHES (F03). */
    duplicateCheckState?: string | null;
    /** Охват векторного индекса, проценты. */
    duplicateCoveragePercent?: number;
    topAdherenceSignal: string | null;
    actions: HomeAction[];
}

const ACTION_META: Record<string, { label: (a: HomeAction) => string; route: string }> = {
    SET_STRATEGY: {label: () => 'Выбрать стратегию', route: '/strategy'},
    // Балл подписан шкалой, количество — в скобках: две разные величины читаются по-разному.
    IMPROVE_ADHERENCE: {
        // score — новое поле; старый сервер кладёт тот же балл в count, поэтому читаем оба.
        label: a => {
            const score = a.score ?? a.count;
            return score != null ? `Подтянуть соответствие — ${score} из 100` : 'Подтянуть соответствие';
        },
        route: '/strategy',
    },
    REVIEW_DISPOSAL: {label: a => `Разобрать выбытие (${a.count ?? 0})`, route: '/disposal'},
    FILL_GAPS: {label: a => `Закрыть пробелы (${a.count ?? 0})`, route: '/insights'},
};

/**
 * F03: «Дубликаты: 0» и «не посчитано» — только два из четырёх возможных состояний. Пока
 * проверена часть гардероба, результат честнее назвать охватом, а не числом.
 */
function duplicatesText(s: HomeSummary): string {
    const coverage = s.duplicateCoveragePercent;
    switch (s.duplicateCheckState) {
        case 'NOT_CHECKED':
            return 'не проверено';
        case 'PARTIAL':
            return `проверено ${coverage ?? 0}%`;
        case 'NO_MATCHES':
            return 'совпадений нет';
        case 'MATCHES':
            return String(s.duplicateGroupsCount);
        default:
            return s.duplicatesNotComputed ? 'не посчитано' : String(s.duplicateGroupsCount);
    }
}

function duplicatesHint(s: HomeSummary): string | undefined {
    const unindexed = s.duplicatesUnindexedCount ?? 0;
    if (unindexed <= 0) {
        return undefined;
    }
    return `Без индекса: ${pluralItems(unindexed)} — дубли по ним появятся после индексации`;
}

/**
 * R12: «— · история копится ещё 150 дней» читалось как таймер доступности продукта, а 150 —
 * окно анализа из стратегии (и вовсе фолбэк фронта, пока бэкенд порог не отдавал). Показываем
 * причину: сколько вещей без отметок носки.
 */
function deadStockPendingText(s: HomeSummary): string {
    const without = s.itemsWithoutWearRecords ?? 0;
    const total = s.activeItemsCount ?? 0;
    return without > 0 && total > 0
        ? `— · нет отметок носки у ${without} из ${total}`
        : '— · ждём отметок носки';
}

/** Подсказка разделяет окно анализа и остаток: это разные числа с разным смыслом. */
function deadStockPendingHint(s: HomeSummary): string {
    const windowDays = s.deadStockThresholdDays;
    const windowPart = windowDays
        ? `Окно анализа «давно не носили» — ${pluralDays(windowDays)} (настройка стратегии). `
        : '';
    const remaining = s.historyDaysRemaining;
    const remainingPart = remaining != null && remaining > 0
        ? `Отмечайте носку — показатель появится раньше; без новых отметок — примерно через ${pluralDays(remaining)}.`
        : 'Показатель появится, как только у вещей будут отметки носки.';
    return windowPart + remainingPart;
}

function scoreColor(score: number): string {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
}

const HomeAutopilotCard: React.FC = () => {
    const {selectedOwnerId} = useWardrobe();
    const navigate = useNavigate();
    const [data, setData] = useState<HomeSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setFailed(false);
            try {
                const api = await createAxiosInstance();
                const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
                const res = await api.get<HomeSummary>('/home/summary', {params});
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedOwnerId]);

    if (loading || failed || !data) {
        return null;
    }

    const s = data;

    return (
        <section className="card p-6" data-testid="home-autopilot">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Сегодня</h2>
                {/* F11: балл капсулы подписан явно — иначе он читается как ещё одно «здоровье». */}
                {s.strategyConfigured && s.adherenceScore != null && (
                    <span className="text-sm text-gray-500 dark:text-gray-400"
                        data-testid="home-adherence"
                        title="Насколько гардероб соответствует выбранной стратегии. Это отдельная метрика: здоровье гардероба считается по-другому.">
                        Соответствие капсуле «{archetypeLabel(s.archetype)}» ·{' '}
                        <span className={`font-semibold ${scoreColor(s.adherenceScore)}`}>
                            {s.adherenceScore}/100
                        </span>
                    </span>
                )}
            </div>

            {s.topAdherenceSignal && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-testid="home-top-signal">
                    {s.topAdherenceSignal}
                </p>
            )}

            <div className="flex flex-wrap gap-2 text-sm mb-4" data-testid="home-counts">
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200">
                    К выбытию: {s.disposalCount}
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200">
                    Пробелы: {s.gapsCount}
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200"
                    data-testid="home-dead-stock"
                    title={(s.deadStockNotComputed || s.deadStockCount == null)
                        ? deadStockPendingHint(s)
                        : undefined}>
                    Залежалось: {(s.deadStockNotComputed || s.deadStockCount == null)
                        ? deadStockPendingText(s)
                        : s.deadStockCount}
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200"
                    data-testid="home-duplicates"
                    title={duplicatesHint(s)}>
                    Дубликаты: {duplicatesText(s)}
                </span>
            </div>

            {s.actions.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400" data-testid="home-allgood">
                    Гардероб под контролем — на сегодня всё. 👌
                </p>
            ) : (
                <div className="flex flex-wrap gap-2" data-testid="home-actions">
                    {s.actions.map(a => {
                        const meta = ACTION_META[a.code];
                        if (!meta) return null;
                        return (
                            <button
                                key={a.code}
                                type="button"
                                onClick={() => navigate(meta.route)}
                                className="min-h-[44px] px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                            >
                                {meta.label(a)}
                            </button>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default HomeAutopilotCard;
